import { gql, QueryResult, useQuery, useSubscription } from '@apollo/client'
import { sum } from 'd3'
import * as React from 'react'
import { useEffect } from 'react'
import styled from 'styled-components'
import { Link } from '../../../../common/src/components/Link'
import { Spacer, H5, BodyText, ColoredSpan, EllipsisDiv } from '../../../../common/src/components/core'
import expandable, { ExpandableProps } from '../../../../common/src/components/expandable'
import { handleError } from '../../../../common/src/data/errors'
import { dateNoYearFmt, dateMDTimeFmt, isToday } from '../../../../common/src/date'
import {
  FetchThreadQuery,
  CalViewBlockType,
  ConfirmationStatus,
  CalResponseStatus,
  FetchThreadCalViewQuery,
  FetchThreadCalViewQueryVariables,
  ShowingUpdatesSubscriptionSubscription,
  ShowingUpdatesSubscriptionSubscriptionVariables,
} from '../../../../common/src/types.gen'
import { showingUpdatesSubscription } from '../../graphql/subscriptions'
import { ConfirmationStatusSpan } from '../components/ShowingStatus'
import { getHash, getTaskPath } from '../route'

const fetchThreadCalView = gql`
  query FetchThreadCalView($threadId: Int!) {
    thread(threadId: $threadId, updateLastRead: false) {
      id
      calView {
        tzName
        blocks {
          id
          type
          dispDate
          dispTime
          snippet
          showingConfStatus
          calRespStatus
          calEvent {
            id
            timeCreated
          }
          showing {
            id
            leasingTaskId
          }
        }
      }
    }
  }
`

interface CalViewProps {
  thread: FetchThreadQuery['thread']
}

interface DispDay {
  date: string
  events: DispEvent[]
}

interface DispEvent {
  type: CalViewBlockType
  time: string
  snippet: string
  id: string
  showingConfStatus?: ConfirmationStatus | null
  calRespStatus?: CalResponseStatus | null
  calTimeCreated?: string | null
  showing?: { id: number; leasingTaskId: number } | null
}

class CalViewImpl extends React.PureComponent<
  CalViewProps & ExpandableProps & QueryResult<FetchThreadCalViewQuery, FetchThreadCalViewQueryVariables>
> {
  render() {
    const { data, isExpanded, toggleExpand } = this.props
    if (!(data && data.thread && data.thread.calView)) {
      return null
    }
    const { blocks } = data.thread.calView

    // Divide calendar events into sections, one per day
    const days = calcDays(blocks, isExpanded)
    const numHidden = blocks.length - sum(days.map(d => d.events.length))

    return (
      <div>
        <Spacer height={32} />
        {days.map((day, i) => (
          <div key={day.date}>
            <H5 color={isToday(day.date) ? 'white' : 'silver'}>{dateNoYearFmt(day.date)}</H5>
            {day.events.map((ev, i) => (
              <CalEventRow key={i} ev={ev} />
            ))}
            <Spacer height={i === days.length - 1 ? 16 : 32} />
          </div>
        ))}
        {(numHidden > 0 || isExpanded) && (
          <BodyText>
            <Link onClick={toggleExpand} color="silver">
              {isExpanded ? 'Collapse events' : '+ ' + numHidden + ' events'}
            </Link>
          </BodyText>
        )}
      </div>
    )
  }
}

function CalEventRow({ ev }: { ev: DispEvent }) {
  return (
    <EventRow title={ev.calTimeCreated ? 'Created ' + dateMDTimeFmt(ev.calTimeCreated) : undefined}>
      {ev.showing ? (
        <Link
          color="silver"
          to={getTaskPath(ev.showing.leasingTaskId) + getHash('showing', ev.showing.id)}
          style={col1}
        >
          {ev.time}
        </Link>
      ) : (
        <ColoredSpan color="silver" style={col1}>
          {ev.type === CalViewBlockType.NOW ? <ColoredSpan color="snow">{ev.time}</ColoredSpan> : ev.time}
        </ColoredSpan>
      )}
      <EllipsisDiv style={[CalViewBlockType.SHOWING, CalViewBlockType.SHOWING_SLOT].includes(ev.type) ? col23 : col2}>
        {ev.type === CalViewBlockType.NOW ? <ColoredSpan color="silver">{ev.snippet}</ColoredSpan> : ev.snippet}
      </EllipsisDiv>
      {ev.showingConfStatus && (
        <ConfirmationStatusSpan
          showingConfStatus={ev.showingConfStatus}
          calRespStatus={ev.calRespStatus}
          style={col3}
        />
      )}
      {ev.type === CalViewBlockType.SHOWING_SLOT && <ColoredSpan color="mint">SLOT</ColoredSpan>}
    </EventRow>
  )
}

function calcDays(
  blocks: NonNullable<FetchThreadCalViewQuery['thread']['calView']>['blocks'],
  isExpanded: boolean
): DispDay[] {
  let numDaysDisplayed = 0
  const days: DispDay[] = []
  for (const block of blocks) {
    const { id, type, dispDate, dispTime, snippet, showingConfStatus, calRespStatus, calEvent, showing } = block
    let currentDay = days[days.length - 1]
    // If the current day has changed, increment numDaysDisplayed and if calView is not expanded, stop if we have reached 4 days.
    if (!currentDay || currentDay.date !== dispDate) {
      if (!isExpanded && numDaysDisplayed >= 4) {
        break
      }
      currentDay = { date: dispDate, events: [] }
      days.push(currentDay)
      numDaysDisplayed++
    }

    currentDay.events.push({
      id,
      type,
      time: dispTime,
      snippet,
      showingConfStatus,
      calRespStatus,
      calTimeCreated: calEvent && calEvent.timeCreated,
      showing,
    })
  }
  return days
}

const col1 = { gridColumn: 1, whiteSpace: 'nowrap' } as React.CSSProperties
const col2 = { gridColumn: 2, whiteSpace: 'nowrap' } as React.CSSProperties
const col3 = { gridColumn: 3, whiteSpace: 'nowrap' } as React.CSSProperties
const col23 = { gridColumn: '2-3', whiteSpace: 'nowrap' } as React.CSSProperties

const EventRow = styled(BodyText)`
  white-space: nowrap;
  margin-top: 8px;
  display: grid;
  grid-template-columns: 96px auto min-content;
  gap: 8px;
`

const ExpandableCalView = expandable(CalViewImpl)

export default function CalView(props: CalViewProps) {
  const { thread } = props
  const pollInterval = 60000 // refetch every minute to update the clock

  const res = useQuery<FetchThreadCalViewQuery, FetchThreadCalViewQueryVariables>(fetchThreadCalView, {
    variables: { threadId: thread.id },
    pollInterval,
  })

  const subscriptionRes = useSubscription<
    ShowingUpdatesSubscriptionSubscription,
    ShowingUpdatesSubscriptionSubscriptionVariables
  >(showingUpdatesSubscription, {
    variables: { contactId: thread.contactThem.id },
  })
  // Refetch if a relevant showing is updated
  useEffect(() => {
    const { data } = subscriptionRes
    if (data != null && data.showingUpdates.length > 0) {
      console.log('[cal view] refetching...')
      res.refetch().catch(handleError)
    }
  }, [subscriptionRes.data])

  return <ExpandableCalView {...props} {...res} />
}
